import { Link, useLocation } from "react-router-dom";
import { IoMdClose } from "react-icons/io";
import { Flex, Text } from "@chakra-ui/react";
import { useSettings } from "hooks/settings";
import { PlanType } from "enum/planTypes";
import { useSubscription, useUser } from "hooks/user";
import { useEffect, useState, useMemo } from "react";
import { ROLES } from "enum/roles";
import { useGetManualInvoices } from "hooks/billing";
import { InvoiceStatus } from "enum/invoiceStatus";

import "./news-letter.scss";
import { useGetAllNotifications } from "hooks/notifications";
import { INotificationResponse } from "types/services";
import { CustomNotificationLocation } from "enum/notifications";
import { Routes } from "constants/routes";
import { REACT_APP_FE_URL } from "constants/constant";

const NewsStrip = () => {
  const { user } = useUser();
  const { subscription, isGettingSubscription } = useSubscription();
  const { settings } = useSettings();
  const location = useLocation();

  const [isDismissed, setIsDismissed] = useState(false);
  const [actveNotification, setActiveNotification] = useState<INotificationResponse>();

  const isPublicPagesRoute = useMemo(() => {
    return !location.pathname.startsWith("/user/") && !location.pathname.startsWith("/admin/");
  }, [location.pathname]);

  const isBetaMode =
    settings?.find?.((item) => item?.settingKey === "beta-version")
      ?.settingValue === "ON";

  // --- Invoice Logic ---
  const { manualInvoices, isLoading: loadingManualInvoices } = useGetManualInvoices();
  const subscriptionIsInReview = useMemo(() => {
    if (loadingManualInvoices || !manualInvoices?.length) return false;
    return manualInvoices[0]?.status === InvoiceStatus.IN_REVIEW;
  }, [loadingManualInvoices, manualInvoices]);

  // --- Custom Notifications Logic ---
  const { notifications } = useGetAllNotifications({ page: 1, limit: 100 });

  useEffect(() => {
    if (!notifications?.length) {
      setActiveNotification(undefined);
      return;
    }

    const targetLocationType = isPublicPagesRoute
      ? CustomNotificationLocation.PUBLIC_PAGES
      : CustomNotificationLocation.USER_DASHBOARD;

    const foundNotification = notifications.find((notification: INotificationResponse) =>
      !!notification?.status &&
      (
        !!notification?.location?.includes(targetLocationType) ||
        notification?.location?.some(notifilocation => location.pathname.startsWith(notifilocation))
      )
    );
    
    setActiveNotification(foundNotification);
  }, [notifications, isPublicPagesRoute, location.pathname]);

  // --- System/Subscription Notification Logic ---
  const getSubscriptionMessageData = useMemo(() => {
    // Suppress system messages for admins, beta mode users, or if data is loading
    if (user?.role === ROLES.SUPER_ADMIN || isBetaMode || isGettingSubscription) return null;
    if (isPublicPagesRoute || location.pathname === Routes.HOME) return null;
    if (isDismissed) return null;

    const remainingDays = +subscription?.remainingDays! + 1;
    const isPastDue = !subscription?.status && remainingDays <= 0;
    const nearingExpiry = !subscription?.status && remainingDays >= 0 && remainingDays <= 6;
    const isCanceledAtPeriodEnd = subscription?.cancel_at_period_end as boolean;
    const isNearingQuotaLimit = !user?.storeSearches || !user?.listingSearches || !user?.marketSearches || !user?.keywordsSearches;

    const billingLink = `${REACT_APP_FE_URL}/user/billing`;
    const showBillingButton = location.pathname !== "/user/billing" && !subscriptionIsInReview;

    // 2. Handle Subscription In Review
    if (subscriptionIsInReview) {
      return {
        message: "Your subscription request is IN REVIEW",
        buttonText: "",
        buttonLink: "",
        backgroundColor: "rgb(255 125 25)",
        showButton: false,
      };
    }

    // 3. Handle Canceled Subscriptions
    if (isCanceledAtPeriodEnd) {
        return {
            message: `Your subscription will be canceled on ${subscription?.canceled_at} as per ${subscription?.cancellation_details?.reason}`,
            buttonText: "",
            buttonLink: "",
            backgroundColor: "#ff4c49",
            showButton: false,
        };
    }

    // 4. Handle Expired Subscriptions
    if (isPastDue) {
        return {
            message: `Your ${subscription?.period === 7 ? "7 days free trial" : "subscription"} has been expired`,
            buttonText: "Renew Now",
            buttonLink: billingLink,
            backgroundColor: "#ff4c49",
            showButton: showBillingButton,
        };
    }

    // 5. Handle Nearing Expiry
    if (nearingExpiry) {
        return {
            message: `Your ${subscription?.period === 7 ? "free trial" : "subscription"} will expire after ${remainingDays} ${remainingDays > 1 ? "days" : "day"}`,
            buttonText: subscription?.period === 7 ? "Purchase Now" : "Renew Now",
            buttonLink: billingLink,
            backgroundColor: "rgb(255 125 25)",
            showButton: showBillingButton,
        };
    }

    // 6. Handle Nearing Quota Limits
    if (isNearingQuotaLimit) {
        return {
            message: "It seems you are approaching the limit of one of your search quota",
            buttonText: subscription?.planType ===  "Upgrade Plan", // More descriptive button text
            buttonLink: billingLink,
            backgroundColor: "rgb(255 125 25)", // Orange color for warning
            showButton: showBillingButton,
        };
    }
    
    return {
        message: "Upgrade today and get access to all exclusive features",
        buttonText: "Try it Now",
        buttonLink: billingLink,
        backgroundColor: "#185ADB",
        showButton: showBillingButton
    };
  }, [user?.role, isBetaMode, isGettingSubscription, isPublicPagesRoute, location.pathname, isDismissed, subscription, subscriptionIsInReview, user?.storeSearches, user?.listingSearches, user?.marketSearches, user?.keywordsSearches, location?.pathname]);

  // --- Render Logic ---

  // Priority 1: Custom Notifications
  if (actveNotification?.id) {
    return (
      <Flex
        className="news-strip"
        style={{ backgroundColor: actveNotification?.backgroundColor, ...(isPublicPagesRoute && { position: "fixed", zIndex: 999 }) }}
      >
        <Text className="text" dangerouslySetInnerHTML={{ __html: actveNotification?.text }} />
        {actveNotification?.buttonText && actveNotification?.buttonLink && location?.pathname !== actveNotification?.buttonLink && (
          <Flex className="news-btn">
            <Link
              to={`${actveNotification.buttonLink}`}
              className="btn-text"
              target="_blank"
            >
             {actveNotification.buttonText}
            </Link>
          </Flex>
        )}
      </Flex>
    );
  }

  // Priority 2: System/Subscription Notifications
  if (getSubscriptionMessageData) {
    const { message, buttonText, buttonLink, backgroundColor, showButton } = getSubscriptionMessageData;

    return (
      <Flex className="news-strip" style={{ backgroundColor }}>
        <Text className="text">
          {message}
        </Text>
        {showButton && (
          <Flex className="news-btn">
            <Link
              to={buttonLink}
              className="btn-text"
            >
              {buttonText}
            </Link>
          </Flex>
        )}
        <Flex>
          <IoMdClose
            className="close-icon"
            onClick={() => { setIsDismissed(true); }}
          />
        </Flex>
      </Flex>
    );
  }

  // Priority 3: Nothing to show
  return <></>;
};

export default NewsStrip;
